import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Receipt as StatementIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import { useLedgerLookups } from '../Journals';
import { fmtDate, getErrorMessage, unwrap } from './ApShared';

const supplierLabel = (acc, look) => {
  if (acc?.supplier) return acc.supplier.name || `Supplier ${acc.supplier_id}`;
  if (acc?.name) return acc.name;
  return look.supplierLabel(acc?.supplier_id ?? acc?.id, `Supplier ${acc?.supplier_id ?? acc?.id ?? ''}`);
};

const accountBalance = (acc) => Number(
  acc?.balance ?? acc?.current_balance ?? acc?.outstanding_balance ?? acc?.balance_due ?? 0
);

const emptyStatement = () => ({
  open: false, accountId: null, label: '', start_date: '', end_date: '',
  loading: false, rows: [], opening: 0, closing: 0,
});

// Per-supplier AP subledger: one running-balance account per synced
// supplier. Read-only here, same as receivables/CustomerLedgerTab.js -
// accounts are created by the party-sync bridge, not by hand. Unlike AR,
// 'ap' doesn't whitelist a separate subsidiary-ledger/entries prefix (see
// config/ledger.php), so the statement reads getSupplierLedgerAccount's own
// detail response - same embedded-transactions shape as
// journal-entries/account-ledger/{accountId} (verified in Journals.js).
const SupplierLedgerTab = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.LEDGER.AP, ACTIONS.READ);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [statement, setStatement] = useState(emptyStatement());

  const loadStatement = async (accountId, label, extra = {}) => {
    try {
      setStatement((p) => ({ ...p, loading: true }));
      const params = {
        ...(extra.start_date ?? statement.start_date ? { start_date: extra.start_date ?? statement.start_date } : {}),
        ...(extra.end_date ?? statement.end_date ? { end_date: extra.end_date ?? statement.end_date } : {}),
      };
      const res = await ledgerAPI.getSupplierLedgerAccount(accountId, params);
      const payload = unwrap(res);
      const rows = payload.transactions ?? payload.entries ?? (Array.isArray(payload) ? payload : []);
      setStatement((p) => ({
        ...p,
        rows,
        opening: Number(payload.opening_balance) || 0,
        closing: Number(payload.closing_balance ?? payload.balance) || 0,
        loading: false,
      }));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch statement'), 'error');
      setStatement((p) => ({ ...p, loading: false }));
    }
  };

  const openStatement = (acc) => {
    const accountId = acc.supplier_id ?? acc.id;
    setStatement({ ...emptyStatement(), open: true, accountId, label: supplierLabel(acc, look) });
    loadStatement(accountId, supplierLabel(acc, look));
  };

  if (!canRead) return null;

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.AP}
        title="Supplier Accounts"
        subtitle="Per-supplier AP balance, synced from bill and payment activity"
        idKey="supplier_id"
        searchPlaceholder="Search supplier name..."
        emptyMessage="No supplier accounts found. Accounts appear here once a supplier is billed or paid."
        fetchItems={(params) => ledgerAPI.getSupplierLedgerAccounts(params)}
        filters={[
          { type: 'numberrange', keyFrom: 'min_balance', keyTo: 'max_balance', label: 'balance' },
        ]}
        columns={[
          { label: 'Supplier', render: (a) => supplierLabel(a, look) },
          { label: 'Balance', render: (a) => formatAccountingAmount(accountBalance(a)), align: 'right' },
        ]}
        rowActions={(a) => [
          { icon: StatementIcon, title: 'View statement', color: 'primary', onClick: () => openStatement(a) },
        ]}
      />

      <Dialog open={statement.open} onClose={() => setStatement(emptyStatement())} maxWidth="lg" fullWidth>
        <DialogTitle>Statement - {statement.label}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} mb={2} alignItems="center">
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="From" value={statement.start_date} onChange={(e) => setStatement((p) => ({ ...p, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" type="date" label="To" value={statement.end_date} onChange={(e) => setStatement((p) => ({ ...p, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button size="small" variant="outlined" onClick={() => loadStatement(statement.accountId, statement.label)}>Reload</Button>
            </Grid>
          </Grid>
          {statement.loading ? (
            <LinearProgress />
          ) : statement.rows.length === 0 ? (
            <Typography color="text.secondary">No posted entries for this supplier in the selected period.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5}><em>Opening balance</em></TableCell>
                  <TableCell align="right"><strong>{formatAccountingAmount(statement.opening)}</strong></TableCell>
                </TableRow>
                {statement.rows.map((r, i) => (
                  <TableRow key={r.entry_id ?? i} hover>
                    <TableCell>{fmtDate(r.entry_date ?? r.transaction_date ?? r.date)}</TableCell>
                    <TableCell>{r.reference || r.invoice_number || r.payment_number || r.entry_number || '-'}</TableCell>
                    <TableCell>{r.description || '-'}</TableCell>
                    <TableCell align="right">{Number(r.debit_amount ?? r.debit) > 0 ? formatAccountingAmount(r.debit_amount ?? r.debit) : '-'}</TableCell>
                    <TableCell align="right">{Number(r.credit_amount ?? r.credit) > 0 ? formatAccountingAmount(r.credit_amount ?? r.credit) : '-'}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(r.running_balance ?? r.balance ?? 0)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5} align="right"><strong>Closing balance</strong></TableCell>
                  <TableCell align="right"><strong>{formatAccountingAmount(statement.closing)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatement(emptyStatement())}>Close</Button>
        </DialogActions>
      </Dialog>

      {SnackbarComponent}
    </>
  );
};

export default SupplierLedgerTab;
